import { useState, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import PageContainer from '~/shared/components/PageContainer';
import Typography from '~/shared/components/Typography';
import UtilizationChart from '~/shared/components/UtilizationCharts/UtilizationChart';
import UsageChart from '~/shared/components/UtilizationCharts/UsageChart';
import { ChartSyncProvider, LineChartHandle } from '~/shared/components/LineChart';
import { useAuthContext } from '~/shared/context/useAuthContext';
import { getOrgAccountsScopeValue } from '~/shared/utils/organizationUtils';
import { isOrganizationView, getOrganizationAccounts } from '~/shared/utils/accountUtils';
import { useVolumeViewPresetCounts } from '~/shared/hooks/useVolumeViewPresetCounts';
import { useActionTypeFilterCounts } from '~/shared/hooks/useActionTypeFilterCounts';
import { useAllActionsCount } from '~/shared/hooks/useAllActionsCount';

import {
  apiFilterKey,
  reportsActionTypeKey,
  reportsDateFilterKey,
  reportsFullGraphKey,
  reportsRefreshStateKey,
} from '../common/serachParamKeys';
import {
  ActionTypeFilterValue,
  parseActionTypeFilter,
} from '~/shared/components/filters/actionTypeFilter';
import ReportsFilterSection from '../ReportsFilter/ReportsFilterSection';
import useUtilizationQueryParams from '../common/useUtilizationQueryParams';

import KPICards from './components/KPICards';
import OptimizationTable from './components/OptimizationTable';
import { buildSavingsQueryUrl } from './helpers';
import { useReportsSearchParamsHandlers } from './hooks/useReportsSearchParamsHandlers';
import { useOptimizationTableParams } from './hooks/useOptimizationTableParams';

const OPTIMIZATION_TABLE_DATA_LIMIT = 50;

export default function ReportsPage() {
  const { accounts, activeTenantId } = useAuthContext();
  const isOrgView = isOrganizationView(activeTenantId, accounts);
  const orgAccounts = useMemo(
    () => getOrganizationAccounts(activeTenantId, accounts, isOrgView),
    [activeTenantId, accounts, isOrgView]
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const activeDateFilter = searchParams.get(reportsDateFilterKey);
  const activeRefreshState = searchParams.get(reportsRefreshStateKey);
  const apiFilters = searchParams.get(apiFilterKey);
  const activeActionType = parseActionTypeFilter(searchParams.get(reportsActionTypeKey));

  const [pageOffset, setPageOffset] = useState('0');
  const [optActionsCount, setOptActionsCount] = useState<number | undefined>(undefined);

  const utilizationChartRef = useRef<LineChartHandle>(null);
  const usageChartRef = useRef<LineChartHandle>(null);

  const { utilizationQueryUrl, onRefetchUtilizationQuery } = useUtilizationQueryParams({
    utilizationDateFilterSearchParam: activeDateFilter,
    showFullGraphSearchParam: searchParams.get(reportsFullGraphKey),
    apiFiltersSearchParam: apiFilters,
    refreshStateSearchParams: activeRefreshState,
  });

  const {
    sortField,
    currentReportFilters,
    parsedFilters,
    optimizationTableQueryString,
    optimizationTableCountQueryString,
    optimizationTableCountBaseQueryString,
    filtersSignature,
    timeSignature,
    resolvedAccountsScope,
  } = useOptimizationTableParams({
    activeDateFilter,
    apiFilters,
    dataLimit: OPTIMIZATION_TABLE_DATA_LIMIT,
    pageOffset,
    activeRefreshState,
    accountsScope: getOrgAccountsScopeValue(accounts, activeTenantId),
    actionType: activeActionType,
  });

  const handleActionTypeChange = useCallback(
    (type: ActionTypeFilterValue) => {
      setPageOffset('0');
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (type) {
            next.set(reportsActionTypeKey, type);
          } else {
            next.delete(reportsActionTypeKey);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const { presetCounts, isLoading: presetCountsLoading } =
    useVolumeViewPresetCounts(currentReportFilters);

  const { typeCounts, isLoading: typeCountsLoading } = useActionTypeFilterCounts({
    baseCountQueryString: optimizationTableCountBaseQueryString,
  });

  const { allActionsCount, allActionsCountLoading } = useAllActionsCount({
    baseCountQueryString: optimizationTableCountBaseQueryString,
  });

  const savingsQueryUrl = useMemo(
    () =>
      buildSavingsQueryUrl(parsedFilters, {
        accountsScope: resolvedAccountsScope,
      }),
    [parsedFilters, resolvedAccountsScope]
  );

  const {
    handleDateFilterChange,
    handleBaseFiltersChange,
    handleRefreshStateChange,
    handleChartDateFilterChange,
    handleOptimizationTableSortChange,
  } = useReportsSearchParamsHandlers({ setPageOffset });

  const handleOptimizationTablePageChange = (newPage: number) => {
    setPageOffset((newPage * OPTIMIZATION_TABLE_DATA_LIMIT).toString());
  };

  return (
    <PageContainer className="pb-0! pt-[18px] 4xl:pt-[30px]">
      <KPICards queryUrl={savingsQueryUrl} accountsScope={resolvedAccountsScope} />

      <div className="mt-3 border-b border-primary-70 sticky top-0 bg-white z-25">
        <ReportsFilterSection
          setPageOffset={setPageOffset}
          activeDateFilterTitle={activeDateFilter}
          currentBaseFilters={currentReportFilters}
          onDateFilterChange={handleDateFilterChange}
          onBaseFiltersChange={handleBaseFiltersChange}
          utilizationChartRef={utilizationChartRef}
          usageChartRef={usageChartRef}
          tableQueryUrl={`/api/v1/opt-log/actions?${optimizationTableQueryString}`}
          tableCountUrl={`/api/v1/opt-log/actions/count?${optimizationTableCountQueryString}`}
          chartQueryUrl={utilizationQueryUrl}
          totalCount={optActionsCount}
          isOrgView={isOrgView}
          accounts={orgAccounts}
          activeOrgId={activeTenantId}
          presetCounts={presetCounts}
          presetCountsLoading={presetCountsLoading}
        />
      </div>

      <div className="px-[30px] py-[20px]">
        <div>
          <Typography fontWeight={500} variant="H5" className="py-2.5">
            Utilization over time
          </Typography>
          <ChartSyncProvider>
            <div className="flex gap-[14px] mt-4">
              <UtilizationChart
                activeDateFilter={activeDateFilter || 'Last 3 hours'}
                activeRefreshState={activeRefreshState || ''}
                queryUrlString={utilizationQueryUrl}
                onClickRefresh={onRefetchUtilizationQuery}
                onSelectRefreshState={handleRefreshStateChange}
                onSelectDateFilter={handleChartDateFilterChange}
                chartRef={utilizationChartRef}
              />
              <UsageChart
                activeDateFilter={activeDateFilter || 'Last 3 hours'}
                activeRefreshState={activeRefreshState || ''}
                queryUrlString={utilizationQueryUrl}
                onClickRefresh={onRefetchUtilizationQuery}
                onSelectRefreshState={handleRefreshStateChange}
                onSelectDateFilter={handleChartDateFilterChange}
                chartRef={usageChartRef}
              />
            </div>
          </ChartSyncProvider>
        </div>
      </div>
      <div className="px-[30px]">
        <OptimizationTable
          tableQueryUrl={`/api/v1/opt-log/actions?${optimizationTableQueryString}`}
          countQueryUrl={`/api/v1/opt-log/actions/count?${optimizationTableCountQueryString}`}
          onPageChange={handleOptimizationTablePageChange}
          onSortChange={handleOptimizationTableSortChange}
          dataLimit={OPTIMIZATION_TABLE_DATA_LIMIT}
          currentSortField={sortField}
          currentOffset={pageOffset}
          timeSignature={timeSignature}
          filtersSignature={filtersSignature}
          onCountChange={setOptActionsCount}
          selectedActionType={activeActionType}
          onActionTypeChange={handleActionTypeChange}
          typeCounts={typeCounts}
          typeCountsLoading={typeCountsLoading}
          allActionsCount={allActionsCount}
          allActionsCountLoading={allActionsCountLoading}
        />
      </div>
    </PageContainer>
  );
}
